import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import {
  CLIENT_REF_KINDS,
  RECONCILE_CORPUS_MAX_LIST_ROWS,
  RECONCILIATION_EXCEPTION_KINDS,
  type AuthoritativeSowRow,
  type ClientRefKind,
  type ReconcileCorpusResponse,
  type ReconciliationException,
  type ReconciliationExceptionKind,
} from '@clm/shared-types';

import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { ListCsvError, parseListCsv, reconcileCorpus } from '../api/reconcile';
import { DataTable, type DataTableColumn } from '../components/DataTable';
import { ErrorNotice } from '../components/ErrorNotice';
import { requireSurface } from '../routes/surfaces';

/**
 * Corpus reconciliation — CLM-35, FR-15 (D-B17). Gated by `contract:bulk_ingest`
 * (Q6), so open to Contributor as well as Admin since R-94-1 widened that
 * action (CLM-94) — the person who reads the exception list is the person who
 * remediates it through Bulk ingest.
 *
 * An admin or contributor pastes (or picks) the Qfly opportunities export, the SPA parses it
 * **client-side** into the contract's list rows, posts them, and renders the
 * report: a run strip, totals, and one table per exception kind present.
 *
 * ## Two rules this page keeps, in the CLM-37 shape
 *
 *  - **The primary action renders whatever the fetch did** (B-33). The form and
 *    its Run button live outside the panel branch; a 501, a 403, a network
 *    failure and an off-contract payload each render distinct copy underneath.
 *  - **Nothing here writes to a contract.** No fix button, no re-sort, no
 *    download: the CSV is the operator CLI's (the viewer sandbox cannot save a
 *    file, and rule 11 keeps the SPA to JSON over the gateway). `missing` rows
 *    link to Bulk ingest, which is where remediation happens.
 *
 * `scope_complete: false` is rendered as a warning, never hidden: a
 * practice-scoped admin's report is honest about what it could not see.
 */

const KIND_LABELS: Readonly<Record<ReconciliationExceptionKind, string>> = {
  missing: 'On the list, not stored',
  missing_party_unresolvable: 'On the list, client not resolvable',
  extra: 'Stored, not on the list',
  party_mismatch: 'Stored under a different client',
  duplicate_stored: 'Stored twice under one client',
  duplicate_document: 'Same document filed twice',
  duplicate_in_list: 'Listed twice',
  list_row_unusable: 'List row not usable',
  stored_without_sow_number: 'Stored without a SOW number',
};

const CLIENT_REF_LABELS: Readonly<Record<ClientRefKind, string>> = {
  sor_reference_id: 'Qfly client id (sor_reference_id) — recommended',
  sow_initials: 'SOW initials (sow_initials)',
  party_key: 'CLM party key (party_key)',
  display_name: 'Client display name (display_name)',
};

type Phase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'running'; readonly rows: number }
  | { readonly kind: 'ready'; readonly report: ReconcileCorpusResponse }
  | {
      readonly kind: 'problem';
      readonly problem: ApiProblemKind;
      readonly detail: string | null;
      readonly correlation_id: string | null;
      readonly status: number | null;
    };

function toProblem(error: unknown): Extract<Phase, { kind: 'problem' }> {
  return {
    kind: 'problem',
    problem: classifyApiError(error),
    detail: error instanceof ApiError ? error.message : null,
    correlation_id: error instanceof ApiError ? error.correlation_id : null,
    status: error instanceof ApiError ? error.status : null,
  };
}

function remediationCell(row: ReconciliationException) {
  switch (row.remediation) {
    case 'plan_ingest':
      return (
        <Link to="/admin/ingest">Plan ingest for {row.party_display_name ?? row.party_key ?? 'this client'}</Link>
      );
    case 'run_party_sync':
      return <>party-sync must run first (operator)</>;
    case 'review_duplicate':
      return <>Review the pair; retire or re-key one</>;
    case 'fix_upstream':
      return <>Fix in Qfly; re-export</>;
    case 'manual_review':
      return <>Manual review</>;
    case 'none':
      return <>—</>;
  }
}

function clientCell(row: ReconciliationException) {
  if (row.party_key === null) return <span className="mut">—</span>;
  return (
    <>
      <code>{row.party_key}</code>
      {row.party_display_name === null ? null : <> {row.party_display_name}</>}
    </>
  );
}

const COLUMNS: readonly DataTableColumn<ReconciliationException>[] = [
  { id: 'sow', header: 'SOW number', cell: (row) => (row.sow_number === null ? <span className="mut">—</span> : <code>{row.sow_number}</code>) },
  { id: 'client', header: 'Client', cell: clientCell },
  { id: 'contract', header: 'Contract', cell: (row) => (row.contract_key === null ? '' : <code>{row.contract_key}</code>) },
  { id: 'other', header: 'Other contract', cell: (row) => (row.other_contract_key === null ? '' : <code>{row.other_contract_key}</code>) },
  { id: 'source', header: 'Source row', cell: (row) => row.source_row_ref ?? '' },
  { id: 'detail', header: 'Detail', cell: (row) => row.detail },
  { id: 'remediation', header: 'Remediation', cell: remediationCell },
];

function rowKey(row: ReconciliationException): string {
  return [row.kind, row.sow_number, row.party_key, row.contract_key, row.other_contract_key, row.source_row_ref].join('|');
}

/* -------------------------------------------------------------------------- */
/* Surface                                                                      */
/* -------------------------------------------------------------------------- */

export function CorpusReconciliation() {
  const surface = requireSurface('reconcile');

  const [sourceLabel, setSourceLabel] = useState('');
  const [clientRefColumn, setClientRefColumn] = useState<ClientRefKind>('sor_reference_id');
  const [csvText, setCsvText] = useState('');
  const [includeMatched, setIncludeMatched] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const abortRef = useRef<AbortController | null>(null);

  const running = phase.kind === 'running';

  function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file === undefined) return;
    if (sourceLabel.trim() === '') setSourceLabel(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      setCsvText(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.onerror = () => {
      setFieldError(`Could not read ${file.name}.`);
    };
    reader.readAsText(file);
  }

  async function run(label: string, rows: readonly AuthoritativeSowRow[], controller: AbortController) {
    try {
      const report = await reconcileCorpus(
        { source_label: label, authoritative_list: [...rows], include_matched: includeMatched },
        controller.signal,
      );
      if (controller.signal.aborted) return;
      setPhase({ kind: 'ready', report });
    } catch (error) {
      if (controller.signal.aborted) return;
      setPhase(toProblem(error));
    }
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const label = sourceLabel.trim();
    if (label === '') {
      setFieldError('Name the source — the export file name is the usual label.');
      return;
    }
    let rows: readonly AuthoritativeSowRow[];
    try {
      rows = parseListCsv(csvText, { clientRefColumn });
    } catch (error) {
      setFieldError(error instanceof ListCsvError ? error.message : error instanceof Error ? error.message : String(error));
      return;
    }
    if (rows.length === 0) {
      setFieldError('The CSV has a header and no rows.');
      return;
    }
    if (rows.length > RECONCILE_CORPUS_MAX_LIST_ROWS) {
      setFieldError(
        `${String(rows.length)} rows is over the ${String(RECONCILE_CORPUS_MAX_LIST_ROWS)}-row limit for this page; run the operator CLI (reconcile-corpus --source csv) for a list this size.`,
      );
      return;
    }
    setFieldError(null);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setPhase({ kind: 'running', rows: rows.length });
    void run(label, rows, controller);
  }

  return (
    <>
      <div className="hd">
        <div>
          <h2 id="reconcile-heading">{surface.label}</h2>
          <p>{surface.summary}</p>
        </div>
      </div>

      <form className="card" onSubmit={onSubmit} aria-labelledby="reconcile-heading" aria-busy={running}>
        <h3>Compare the stored corpus with the authoritative SOW list</h3>
        <p>
          Paste the Qfly opportunities export (or choose the file). The list is parsed here and
          posted to the API; nothing about a contract changes — the report is the result, and one
          audit row records that the comparison ran.
        </p>
        <div className="frow">
          <div className="field grow">
            <label htmlFor="rc-source-label">Source label</label>
            <input
              id="rc-source-label"
              name="source_label"
              required
              autoComplete="off"
              placeholder="qfly-opportunities-2026-09-09.csv"
              value={sourceLabel}
              onChange={(event) => setSourceLabel(event.target.value)}
              disabled={running}
            />
          </div>
          <div className="field">
            <label htmlFor="rc-client-ref">Client reference column</label>
            <select
              id="rc-client-ref"
              name="client_ref_column"
              value={clientRefColumn}
              onChange={(event) => setClientRefColumn(event.target.value as ClientRefKind)}
              disabled={running}
            >
              {CLIENT_REF_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {CLIENT_REF_LABELS[kind]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="field">
          <label htmlFor="rc-list">Authoritative list (CSV: sow_number, the client column, optional status)</label>
          <textarea
            id="rc-list"
            name="authoritative_list_csv"
            className="rc-list"
            spellCheck={false}
            placeholder={'sow_number,sor_reference_id,status\nCGISOW26201OV,qfly-cgi,Closed Won'}
            value={csvText}
            onChange={(event) => setCsvText(event.target.value)}
            disabled={running}
          />
        </div>
        <div className="frow">
          <div className="field">
            <label htmlFor="rc-file">Or choose the export file</label>
            <input id="rc-file" name="list_file" type="file" accept=".csv,text/csv" onChange={onFile} disabled={running} />
          </div>
          <label className="rc-check" htmlFor="rc-include-matched">
            <input
              id="rc-include-matched"
              name="include_matched"
              type="checkbox"
              checked={includeMatched}
              onChange={(event) => setIncludeMatched(event.target.checked)}
              disabled={running}
            />
            Include matched SOWs in the report
          </label>
          <button className="btn pri" type="submit" disabled={running}>
            {running ? 'Comparing…' : 'Run reconciliation'}
          </button>
        </div>
        {fieldError ? (
          <p className="ferr" role="alert">
            {fieldError}
          </p>
        ) : null}
        <p className="mut">
          The CSV report is the operator CLI&apos;s (<code>reconcile-corpus --source csv</code>); this page
          shows the same exceptions and downloads nothing. Every run writes one <code>clm.audit_log</code> row
          and no contract row.
        </p>
      </form>

      {phase.kind === 'running' ? (
        <p role="status" aria-busy="true">
          Comparing {phase.rows} list rows with the stored corpus…
        </p>
      ) : null}

      {phase.kind === 'problem' ? <Problem phase={phase} /> : null}

      {phase.kind === 'ready' ? <Report report={phase.report} /> : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Problem                                                                      */
/* -------------------------------------------------------------------------- */

function Problem({ phase }: { readonly phase: Extract<Phase, { kind: 'problem' }> }) {
  if (phase.problem === 'not_implemented') {
    return (
      <ErrorNotice
        kind="not_implemented"
        detail={phase.detail}
        correlation_id={phase.correlation_id}
        action={
          <p className="mut">
            The endpoint is <code>POST /v1/admin/corpus/reconcile</code>. Until it is live, run the
            operator CLI against the database directly.
          </p>
        }
      />
    );
  }
  if (phase.problem === 'forbidden') {
    return (
      <ErrorNotice
        kind="forbidden"
        detail={phase.detail}
        correlation_id={phase.correlation_id}
        action={<p className="mut">Reconciliation needs the Contributor or Admin role (<code>contract:bulk_ingest</code>).</p>}
      />
    );
  }
  if (phase.problem === 'validation') {
    return (
      <ErrorNotice
        kind="validation"
        detail={phase.detail}
        correlation_id={phase.correlation_id}
        action={
          <p className="mut">
            A list over {RECONCILE_CORPUS_MAX_LIST_ROWS} rows is refused here by design — run the
            operator CLI for it.
          </p>
        }
      />
    );
  }
  if (phase.status === 413) {
    return (
      <div className="banner bad" role="alert">
        <p className="notice-head">
          <strong>The list is too large for this page</strong>
        </p>
        <p>
          The gateway refused the request before reading it — the body is over the transport cap.
          Run the operator CLI (<code>reconcile-corpus --source csv</code>) for a list this size.
        </p>
        {phase.correlation_id ? (
          <p className="mut">
            Correlation id <code>{phase.correlation_id}</code>
          </p>
        ) : null}
      </div>
    );
  }
  return <ErrorNotice kind={phase.problem} detail={phase.detail} correlation_id={phase.correlation_id} />;
}

/* -------------------------------------------------------------------------- */
/* Report                                                                       */
/* -------------------------------------------------------------------------- */

function Report({ report }: { readonly report: ReconcileCorpusResponse }) {
  const { run, totals, exceptions } = report;
  const duplicates = totals.duplicate_stored + totals.duplicate_document;
  const quality = totals.duplicate_in_list + totals.list_row_unusable + totals.stored_without_sow_number;
  const kindsPresent = RECONCILIATION_EXCEPTION_KINDS.filter((kind) => exceptions.some((row) => row.kind === kind));

  return (
    <section aria-label="Reconciliation report">
      <dl className="rc-run">
        <div>
          <dt>Source</dt>
          <dd>{run.source.label}</dd>
        </div>
        <div>
          <dt>List digest</dt>
          <dd>
            <code title={run.source.list_sha256}>{run.source.list_sha256.slice(0, 12)}…</code>{' '}
            <CopyButton value={run.source.list_sha256} label="Copy the full list digest" />
          </dd>
        </div>
        <div>
          <dt>Ran (UTC)</dt>
          <dd>{run.ran_utc}</dd>
        </div>
        <div>
          <dt>Audit row</dt>
          <dd>
            <code>{run.audit_id}</code>
          </dd>
        </div>
        <div>
          <dt>Correlation id</dt>
          <dd>
            <code>{run.correlation_id}</code>
          </dd>
        </div>
      </dl>

      {run.scope_complete ? null : (
        <ErrorNotice
          kind="forbidden"
          action={
            <p>
              Your scope covers only some practices; this report cannot say the corpus has no gaps. Run
              it with Scope-All or from the operator CLI.
            </p>
          }
        />
      )}

      <p className="rc-verdict" data-tone={totals.gaps > 0 ? 'warn' : 'ok'} role="status">
        {totals.gaps > 0 ? (
          <>
            <strong>{totals.gaps} gaps</strong> — this corpus is not reconciled against {run.source.label}.
          </>
        ) : (
          <>
            <strong>No gaps</strong> — reconciled against {run.source.label}.
          </>
        )}
      </p>

      <div className="totals" data-tone={totals.gaps > 0 ? 'warn' : 'plain'}>
        <Total label="Matched" value={totals.matched} />
        <Total label="Missing" value={totals.missing} warn />
        <Total label="Party unresolvable" value={totals.missing_party_unresolvable} warn />
        <Total label="Extra" value={totals.extra} warn />
        <Total label="Party mismatch" value={totals.party_mismatch} warn />
        <Total label="Duplicates" value={duplicates} warn />
        <Total label="List quality" value={quality} />
      </div>

      {exceptions.length === 0 ? (
        <div className="dt-empty">
          <p>
            <strong>No exceptions.</strong> {totals.matched} SOWs matched the list.
          </p>
        </div>
      ) : (
        kindsPresent.map((kind) => {
          const rows = exceptions.filter((row) => row.kind === kind);
          return (
            <div key={kind}>
              <h3 className="rc-kind">
                {KIND_LABELS[kind]}
                <span className="tag out">{kind}</span>
                <span className="tag">{rows.length}</span>
              </h3>
              <DataTable
                caption={KIND_LABELS[kind]}
                columns={COLUMNS}
                rows={rows}
                rowKey={rowKey}
                empty={<p>None.</p>}
              />
            </div>
          );
        })
      )}
    </section>
  );
}

function Total({ label, value, warn = false }: { readonly label: string; readonly value: number; readonly warn?: boolean }) {
  return (
    <div className="total" data-tone={warn && value > 0 ? 'warn' : 'plain'}>
      <span>{label}</span>
      <b>{value}</b>
    </div>
  );
}

function CopyButton({ value, label }: { readonly value: string; readonly label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="btn sm qt"
      aria-label={label}
      onClick={() => {
        const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
        if (clipboard === undefined) return;
        void clipboard.writeText(value).then(() => {
          setCopied(true);
        });
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}
